import React, { useState, useEffect } from 'react';
import { Card, Button, Badge, PageHeader, Input } from '../../components/ui';
import api from '../../services/api';

const PLAN_NAMES = { pack_one: 'Pack One', pack_two: 'Pack Two', pack_advanced: 'Pack Advanced' };
const STATUS_COLOR = { completed: 'success', pending: 'warning', failed: 'danger', refunded: 'info' };

const MS = {
  name:    'MS Discoveries',
  address: 'Pothananur, Paramathi Velur',
  city:    'Tamil Nadu 638182',
  country: 'India',
  gstin:   '33FYQPM9398C1ZP',
  email:   'support@msdiscoveries.com',
  phone:   '+91 9443724724',
};

const calcGST = (amount, client) => {
  const country = client?.country || 'India';
  const state   = client?.state || '';
  if (country !== 'India') return { type: 'none', rate: 0, sgst: 0, cgst: 0, igst: 0, total: 0 };
  const base = parseFloat(amount) || 0;
  if (state === 'Tamil Nadu') {
    const each = +(base * 0.09).toFixed(2);
    return { type: 'sgst_cgst', rate: 18, sgst: each, cgst: each, igst: 0, total: each * 2 };
  }
  const igst = +(base * 0.18).toFixed(2);
  return { type: 'igst', rate: 18, sgst: 0, cgst: 0, igst, total: igst };
};

export default function AdminInvoices() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [viewing, setViewing]   = useState(null);
  const [search, setSearch]     = useState('');

  useEffect(() => { fetchInvoices(); }, []);

  const fetchInvoices = async () => {
    try { const r = await api.get('/superadmin/invoices'); setPayments(r.data.payments || []); }
    catch {} finally { setLoading(false); }
  };

  const printInvoice = () => window.print();

  const exportAllCSV = () => {
    const rows = [
      'Invoice No,Client,Company,Email,Plan,Amount,GST Type,GST Amount,Grand Total,Status,Date',
      ...payments.map(p => {
        const gst = calcGST(p.amount, p.client);
        const grand = (parseFloat(p.amount) + gst.total).toFixed(2);
        const isIndia = (p.client?.country || 'India') === 'India';
        return `INV-${p.id?.slice(0,8).toUpperCase()},"${p.client?.name||''}","${p.client?.company||''}",${p.client?.email||''},${PLAN_NAMES[p.planId]||p.planId},${isIndia?'₹':'€'}${p.amount},${gst.type==='none'?'N/A':gst.type==='sgst_cgst'?'SGST+CGST':'IGST'},${isIndia?'₹':'€'}${gst.total.toFixed(2)},${isIndia?'₹':'€'}${grand},${p.status},${p.createdAt?new Date(p.createdAt).toLocaleDateString('en-IN'):''}`;
      })
    ].join('\n');
    const blob = new Blob([rows], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a'); a.href = url; a.download = 'all-invoices.csv'; a.click();
  };

  const filtered = payments.filter(p =>
    !search ||
    p.client?.name?.toLowerCase().includes(search.toLowerCase()) ||
    p.client?.email?.toLowerCase().includes(search.toLowerCase()) ||
    p.client?.company?.toLowerCase().includes(search.toLowerCase()) ||
    `INV-${p.id?.slice(0,8).toUpperCase()}`.toLowerCase().includes(search.toLowerCase())
  );

  const totalRevenue = payments.reduce((s, p) => {
    const gst = calcGST(p.amount, p.client);
    return s + parseFloat(p.amount) + gst.total;
  }, 0);

  /* ── INVOICE PRINT VIEW ── */
  const InvoiceView = ({ payment }) => {
    const client     = payment.client || {};
    const invoiceNo  = `INV-${payment.id?.slice(0,8).toUpperCase()}`;
    const issueDate  = new Date(payment.createdAt);
    const periodEnd  = new Date(issueDate); periodEnd.setMonth(periodEnd.getMonth() + 1);
    const gst        = calcGST(payment.amount, client);
    const grandTotal = (parseFloat(payment.amount) + gst.total).toFixed(2);
    const isIndia    = (client?.country || 'India') === 'India';
    const isTN       = client?.state === 'Tamil Nadu';

    return (
      <div id="invoice-print" style={{ background: '#111', border: '1px solid rgba(46,204,113,0.2)', borderRadius: 20, padding: 44, maxWidth: 720, margin: '0 auto' }}>
        <style>{`
          @media print {
            body * { visibility: hidden !important; }
            #invoice-print, #invoice-print * { visibility: visible !important; }
            #invoice-print { position: fixed; top: 0; left: 0; width: 100%; background: #fff !important; color: #000 !important; padding: 32px !important; border: none !important; border-radius: 0 !important; }
            .no-print { display: none !important; }
            * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        `}</style>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32, paddingBottom: 24, borderBottom: '1px solid rgba(46,204,113,0.15)' }}>
          <div>
            <div style={{ marginBottom: 10 }}>
              <span style={{ fontSize: 28, fontWeight: 900, color: '#2ECC71', letterSpacing: -1 }}>MARK</span>
              <span style={{ fontSize: 13, color: '#888', marginLeft: 8 }}>Ms Discoveries</span>
            </div>
            <div style={{ fontSize: 12, color: '#666', lineHeight: 2 }}>
              {MS.name}<br />
              {MS.address}, {MS.city}<br />
              {MS.country}<br />
              GSTIN: <strong style={{ color: '#ccc' }}>{MS.gstin}</strong><br />
              {MS.email} · {MS.phone}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 30, fontWeight: 900, color: '#fff', marginBottom: 6 }}>INVOICE</div>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#2ECC71', marginBottom: 10 }}>{invoiceNo}</div>
            <Badge color={STATUS_COLOR[payment.status] || 'gray'} style={{ fontSize: 12, padding: '4px 14px' }}>
              {payment.status?.toUpperCase()}
            </Badge>
          </div>
        </div>

        {/* Bill To / Invoice Details */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32, marginBottom: 32 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 800, color: '#2ECC71', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 10 }}>Bill To</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#fff', marginBottom: 4 }}>{client.name}</div>
            <div style={{ fontSize: 13, color: '#888', lineHeight: 1.9 }}>
              {client.company}<br />
              {client.address && <>{client.address}<br /></>}
              {client.city && <>{client.city}, </>}{client.state && <>{client.state}<br /></>}
              {client.pincode && <>{client.pincode} · </>}{client.country || 'India'}<br />
              {client.email}<br />
              {client.phone}
              {client.gstNumber && <><br />GSTIN: <strong style={{ color: '#F39C12' }}>{client.gstNumber}</strong></>}
              {isIndia && !client.gstNumber && <><br /><span style={{ color: '#555', fontSize: 11 }}>Consumer Sale (No GSTIN)</span></>}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 800, color: '#2ECC71', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 10 }}>Invoice Details</div>
            {[
              ['Invoice No',     invoiceNo],
              ['Issue Date',     issueDate.toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })],
              ['Service Period', `${issueDate.toLocaleDateString('en-IN',{day:'2-digit',month:'short'})} – ${periodEnd.toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}`],
              ['Place of Supply', client.state || client.country || 'India'],
              ['Payment Ref',    payment.gatewayOrderId || 'Bank Transfer'],
            ].map(([l, v]) => (
              <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid rgba(255,255,255,0.04)', fontSize: 12 }}>
                <span style={{ color: '#666' }}>{l}</span>
                <span style={{ color: '#ccc', fontWeight: 500 }}>{v}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Line Items */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '3fr 1fr 1fr 1fr', background: 'rgba(46,204,113,0.08)', borderRadius: '10px 10px 0 0', padding: '10px 16px', fontSize: 11, fontWeight: 800, color: '#2ECC71', textTransform: 'uppercase', letterSpacing: 1 }}>
            <span>Description</span><span style={{ textAlign:'center' }}>Qty</span><span style={{ textAlign:'center' }}>Unit Price</span><span style={{ textAlign:'right' }}>Amount</span>
          </div>
          <div style={{ border: '1px solid rgba(46,204,113,0.1)', borderTop: 'none', borderRadius: '0 0 10px 10px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '3fr 1fr 1fr 1fr', padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.04)', fontSize: 13 }}>
              <div>
                <div style={{ fontWeight: 700, color: '#fff', marginBottom: 2 }}>{PLAN_NAMES[payment.planId] || payment.planId} — Monthly Subscription</div>
                <div style={{ fontSize: 11, color: '#555' }}>EmailMark Platform · {issueDate.toLocaleDateString('en-IN', { month:'long', year:'numeric' })}</div>
                <div style={{ fontSize: 11, color: '#555', marginTop: 2 }}>SAC Code: 998314 · IT Software Services</div>
              </div>
              <div style={{ textAlign: 'center', color: '#888', alignSelf: 'center' }}>1</div>
              <div style={{ textAlign: 'center', color: '#888', alignSelf: 'center' }}>{isIndia ? `₹${payment.amount}` : `€${payment.amount}`}</div>
              <div style={{ textAlign: 'right', fontWeight: 700, color: '#fff', alignSelf: 'center' }}>{isIndia ? `₹${payment.amount}` : `€${payment.amount}`}</div>
            </div>
            {[
              ['Contacts Upload',      payment.planId==='pack_one'?'2,000':payment.planId==='pack_two'?'30,000':'Unlimited'],
              ['Email Sends/mo',       payment.planId==='pack_one'?'6,000':payment.planId==='pack_two'?'90,000':'150,000'],
              ['Email Verifications',  payment.planId==='pack_one'?'2,000':payment.planId==='pack_two'?'30,000':'50,000'],
              ['Email Accounts',       payment.planId==='pack_one'?'Up to 10':payment.planId==='pack_two'?'Up to 100':'Unlimited'],
            ].map(([feat, val]) => (
              <div key={feat} style={{ display: 'grid', gridTemplateColumns: '3fr 1fr 1fr 1fr', padding: '7px 16px', borderBottom: '1px solid rgba(255,255,255,0.02)', fontSize: 12 }}>
                <div style={{ color: '#555', paddingLeft: 12 }}>↳ {feat}</div>
                <div style={{ textAlign:'center', color:'#2ECC71', fontSize:11 }}>{val}</div>
                <div style={{ textAlign:'center', color:'#444' }}>incl.</div>
                <div style={{ textAlign:'right', color:'#444' }}>—</div>
              </div>
            ))}
          </div>
        </div>

        {/* Totals */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 28 }}>
          <div style={{ width: 300 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.04)', fontSize: 13 }}>
              <span style={{ color: '#666' }}>Subtotal (Taxable Value)</span>
              <span style={{ color: '#888' }}>{isIndia ? `₹${payment.amount}` : `€${payment.amount}`}</span>
            </div>
            {gst.type === 'sgst_cgst' && (<>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,0.04)', fontSize: 13 }}>
                <span style={{ color: '#666' }}>SGST @ 9%</span><span style={{ color: '#F39C12' }}>₹{gst.sgst}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,0.04)', fontSize: 13 }}>
                <span style={{ color: '#666' }}>CGST @ 9%</span><span style={{ color: '#F39C12' }}>₹{gst.cgst}</span>
              </div>
            </>)}
            {gst.type === 'igst' && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,0.04)', fontSize: 13 }}>
                <span style={{ color: '#666' }}>IGST @ 18%</span><span style={{ color: '#F39C12' }}>₹{gst.igst}</span>
              </div>
            )}
            {gst.type === 'none' && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,0.04)', fontSize: 13 }}>
                <span style={{ color: '#666' }}>GST</span><span style={{ color: '#888' }}>Not Applicable (International)</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '14px 16px', background: 'rgba(46,204,113,0.1)', borderRadius: 10, marginTop: 10, border: '1px solid rgba(46,204,113,0.2)' }}>
              <span style={{ fontWeight: 800, fontSize: 15, color: '#fff' }}>Total Amount</span>
              <span style={{ fontWeight: 900, fontSize: 20, color: '#2ECC71' }}>{isIndia ? `₹${grandTotal}` : `€${grandTotal}`}</span>
            </div>
            {payment.status === 'completed' && (
              <div style={{ textAlign: 'center', marginTop: 10, padding: '8px', background: 'rgba(46,204,113,0.08)', borderRadius: 8, border: '1px solid rgba(46,204,113,0.2)' }}>
                <span style={{ fontSize: 13, color: '#2ECC71', fontWeight: 800 }}>✅ PAYMENT RECEIVED</span>
              </div>
            )}
          </div>
        </div>

        {isIndia && (
          <div style={{ background: 'rgba(243,156,18,0.05)', border: '1px solid rgba(243,156,18,0.15)', borderRadius: 10, padding: '10px 16px', fontSize: 11, color: '#888', marginBottom: 20 }}>
            <strong style={{ color: '#F39C12' }}>GST Note: </strong>
            {isTN ? 'Intra-state supply. SGST & CGST applicable as per GST Act.' : 'Inter-state supply. IGST applicable as per GST Act.'}
            {' '}SAC: 998314 · MS Discoveries GSTIN: {MS.gstin}
          </div>
        )}

        <div style={{ borderTop: '1px solid rgba(46,204,113,0.1)', paddingTop: 18, textAlign: 'center' }}>
          <p style={{ fontSize: 12, color: '#555', lineHeight: 1.9 }}>
            Thank you for your business! · {MS.email} · {MS.phone}<br />
            {MS.name} · {MS.address}, {MS.city} · GSTIN: {MS.gstin}
          </p>
        </div>

        <div className="no-print" style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 24 }}>
          <Button variant="secondary" onClick={() => setViewing(null)}>← Back to All Invoices</Button>
          <Button onClick={printInvoice}>🖨️ Print / Save as PDF</Button>
        </div>
      </div>
    );
  };

  if (viewing) return (
    <div>
      <div className="no-print" style={{ marginBottom: 20 }}>
        <PageHeader title="Invoice" subtitle={`INV-${viewing.id?.slice(0,8).toUpperCase()} · ${viewing.client?.name || viewing.client?.email || ''}`} />
      </div>
      <InvoiceView payment={viewing} />
    </div>
  );

  return (
    <div>
      <PageHeader
        title="All Client Invoices"
        subtitle={`${payments.length} invoices · Total collected: ₹${totalRevenue.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`}
        action={
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" onClick={exportAllCSV}>⬇ Export All CSV</Button>
          </div>
        }
      />

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16, marginBottom: 24 }}>
        {[
          ['🧾', 'Total Invoices', payments.length, '#2ECC71'],
          ['💰', 'Total Collected', `₹${payments.reduce((s,p) => { const g = calcGST(p.amount,p.client); return s+parseFloat(p.amount)+g.total; }, 0).toLocaleString('en-IN', { maximumFractionDigits:2 })}`, '#F39C12'],
          ['📦', 'Unique Clients', new Set(payments.map(p=>p.userId)).size, '#3498DB'],
        ].map(([icon, label, value, color]) => (
          <Card key={label} style={{ textAlign:'center', padding:'20px 12px' }}>
            <div style={{ fontSize: 24 }}>{icon}</div>
            <div style={{ fontSize: 22, fontWeight: 900, color, marginTop: 6 }}>{value}</div>
            <div style={{ fontSize: 11, color: '#555', textTransform: 'uppercase', letterSpacing: 0.8 }}>{label}</div>
          </Card>
        ))}
      </div>

      {/* Search */}
      <div style={{ marginBottom: 16 }}>
        <Input placeholder="🔍 Search by client name, email, company or invoice number..." value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {loading ? <div style={{ color:'#555', textAlign:'center', padding:40 }}>Loading invoices...</div> : (
        filtered.length === 0 ? (
          <Card style={{ textAlign:'center', padding:60 }}>
            <div style={{ fontSize:48, marginBottom:16 }}>🧾</div>
            <h3 style={{ color:'#ccc', marginBottom:8 }}>No invoices found</h3>
            <p style={{ color:'#555', fontSize:14 }}>Completed payments from clients will appear here.</p>
          </Card>
        ) : (
          <Card style={{ padding:0, overflow:'hidden' }}>
            <div style={{ display:'grid', gridTemplateColumns:'1.2fr 1.5fr 1.5fr 1fr 1fr 1fr 1fr 1fr', padding:'12px 20px', background:'rgba(46,204,113,0.05)', borderBottom:'1px solid rgba(46,204,113,0.1)', fontSize:11, fontWeight:800, color:'#555', textTransform:'uppercase', letterSpacing:0.8 }}>
              <span>Invoice</span><span>Client</span><span>Company</span><span>Plan</span><span>Amount</span><span>GST</span><span>Total</span><span style={{ textAlign:'right' }}>Date / Action</span>
            </div>
            {filtered.map((p, i) => {
              const gst     = calcGST(p.amount, p.client);
              const isIndia = (p.client?.country || 'India') === 'India';
              const grand   = (parseFloat(p.amount) + gst.total).toFixed(2);
              return (
                <div key={p.id} style={{ display:'grid', gridTemplateColumns:'1.2fr 1.5fr 1.5fr 1fr 1fr 1fr 1fr 1fr', padding:'13px 20px', borderBottom: i<filtered.length-1?'1px solid rgba(255,255,255,0.03)':'none', alignItems:'center', transition:'background 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.02)'}
                  onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                  <span style={{ fontSize:12, fontWeight:700, color:'#2ECC71' }}>INV-{p.id?.slice(0,8).toUpperCase()}</span>
                  <div>
                    <div style={{ fontSize:13, color:'#ccc', fontWeight:600 }}>{p.client?.name || '—'}</div>
                    <div style={{ fontSize:11, color:'#555' }}>{p.client?.email}</div>
                  </div>
                  <span style={{ fontSize:12, color:'#888' }}>{p.client?.company || '—'}</span>
                  <span style={{ fontSize:12, color:'#ccc' }}>{PLAN_NAMES[p.planId] || p.planId}</span>
                  <span style={{ fontSize:13, fontWeight:700, color:'#fff' }}>{isIndia ? `₹${p.amount}` : `€${p.amount}`}</span>
                  <span style={{ fontSize:12, color:'#F39C12' }}>{gst.type==='none'?'N/A':gst.type==='sgst_cgst'?'SGST+CGST':'IGST'} {gst.type!=='none'?`₹${gst.total.toFixed(0)}`:''}</span>
                  <span style={{ fontSize:13, fontWeight:800, color:'#2ECC71' }}>{isIndia ? `₹${grand}` : `€${grand}`}</span>
                  <div style={{ textAlign:'right' }}>
                    <div style={{ fontSize:11, color:'#555', marginBottom:4 }}>{p.createdAt ? new Date(p.createdAt).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—'}</div>
                    <Button size="sm" variant="outline" onClick={() => setViewing(p)}>🧾 View</Button>
                  </div>
                </div>
              );
            })}
          </Card>
        )
      )}
    </div>
  );
}
